import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Phone, Video, Info, Maximize2, X } from 'lucide-react';
import './App.css';
import { parseInstagramHTML } from './utils/parser';

// Simple Timezone Map
const TIMEZONES = [
  { label: 'GMT (UTC)', value: 'UTC' },
  { label: 'India (IST)', value: 'Asia/Kolkata' },
  { label: 'US Eastern (EST)', value: 'America/New_York' },
  { label: 'US Pacific (PST)', value: 'America/Los_Angeles' },
  { label: 'London (BST/GMT)', value: 'Europe/London' },
  { label: 'Japan (JST)', value: 'Asia/Tokyo' },
];

function App() {
  // Multi-chat state
  const [chats, setChats] = useState([]); // Array of chat objects
  const [selectedChatId, setSelectedChatId] = useState(null);
  
  // Single chat state (when viewing a specific chat)
  const [messages, setMessages] = useState([]);
  const [myUsername, setMyUsername] = useState('');
  const [fileLoaded, setFileLoaded] = useState(false);
  const [timezone, setTimezone] = useState('UTC');
  const [error, setError] = useState('');
  const [mediaFiles, setMediaFiles] = useState({});
  const [fullscreenMedia, setFullscreenMedia] = useState(null);
  const [allImages, setAllImages] = useState([]);
  const [allVideos, setAllVideos] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [folderName, setFolderName] = useState('');
  const chatEndRef = useRef(null);
  const videoRefs = useRef({});
  const fileInputRef = useRef(null);

  // Scroll to bottom on load
  useEffect(() => {
    if (fileLoaded && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'auto' });
    }
  }, [messages, fileLoaded]);

  // Cleanup object URLs when component unmounts
  useEffect(() => {
    return () => {
      Object.values(mediaFiles).forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [mediaFiles]);

  // Helper function to strip Instagram path prefix
  const stripInstagramPrefix = (path) => {
    if (!path) return path;
    // Remove "your_instagram_activity/messages/inbox/" prefix
    const prefixes = [
      'your_instagram_activity/messages/inbox/',
      'your_instagram_activity\\messages\\inbox\\',
      '../your_instagram_activity/messages/inbox/',
      '../your_instagram_activity\\messages\\inbox\\'
    ];
    
    let cleanPath = path;
    for (const prefix of prefixes) {
      if (path.includes(prefix)) {
        cleanPath = path.substring(path.indexOf(prefix) + prefix.length);
        break;
      }
    }
    return cleanPath;
  };

  // Process entire inbox folder with multiple chat folders
  const processInboxFolder = (files) => {
    console.log('Processing inbox folder with', files.length, 'files');
    
    // Group files by chat folder
    const chatFolders = {};
    
    files.forEach(file => {
      const relativePath = file.webkitRelativePath || file.name;
      console.log('Processing file:', relativePath);
      
      // Strip Instagram prefix first
      const cleanPath = stripInstagramPrefix(relativePath);
      console.log('Clean path:', cleanPath);
      
      // Split by both forward and backward slashes
      const pathParts = cleanPath.replace(/\\/g, '/').split('/');
      console.log('Path parts:', pathParts);
      
      // The structure should be: chatFolder/message.html or chatFolder/photos/file.jpg
      // We need at least 2 parts: chatFolder and filename (or subfolder/filename)
      if (pathParts.length >= 2) {
        const chatFolder = pathParts[0];
        
        if (!chatFolders[chatFolder]) {
          chatFolders[chatFolder] = [];
          console.log('Created chat folder:', chatFolder);
        }
        chatFolders[chatFolder].push(file);
      } else {
        console.log('Skipping file with insufficient path depth:', cleanPath);
      }
    });
    
    console.log('Found chat folders:', Object.keys(chatFolders));
    console.log('Chat folder details:', Object.keys(chatFolders).map(k => `${k}: ${chatFolders[k].length} files`));
    
    // Process each chat folder
    const chatPromises = Object.keys(chatFolders).map(chatFolderName => {
      return processSingleChatFolder(chatFolderName, chatFolders[chatFolderName]);
    });
    
    Promise.all(chatPromises).then(processedChats => {
      const validChats = processedChats.filter(chat => chat !== null);
      console.log('Processed chats:', validChats.length);
      
      if (validChats.length === 0) {
        setError('No valid chats found. Please check the folder structure.');
      } else {
        setChats(validChats);
        setFileLoaded(true);
        setError('');
        // Select first chat by default - directly set state since chats hasn't updated yet
        if (validChats.length > 0) {
          const firstChat = validChats[0];
          setSelectedChatId(firstChat.id);
          setMessages(firstChat.messages);
          setMediaFiles(firstChat.mediaFiles);
          setAllImages(firstChat.images);
          setAllVideos(firstChat.videos);
          setFolderName(firstChat.folderName);
        }
      }
    }).catch(err => {
      console.error('Error processing chats:', err);
      setError('Error processing inbox folder: ' + err.message);
      setFileLoaded(false);
    });
  };
  
  // Process a single chat folder
  const processSingleChatFolder = (chatFolderName, files) => {
    return new Promise((resolve) => {
      console.log(`Processing chat folder: ${chatFolderName} with ${files.length} files`);
      
      // Find the HTML file
      const htmlFile = files.find(f => f.name.toLowerCase().endsWith('.html'));
      if (!htmlFile) {
        console.log('No HTML file found in', chatFolderName);
        resolve(null);
        return;
      }
      
      console.log(`Found HTML file: ${htmlFile.name}`);

      // Create object URLs for media files
      const mediaFileMap = {};
      const images = [];
      const videos = [];
      
      files.forEach(file => {
        if (!file.name.toLowerCase().endsWith('.html')) {
          const url = URL.createObjectURL(file);
          const filename = file.name;
          const lowerFilename = filename.toLowerCase();
          
          const relativePath = file.webkitRelativePath || filename;
          const cleanPath = stripInstagramPrefix(relativePath).replace(/\\/g, '/');
          
          // Remove chat folder name from path for internal matching
          // This creates paths like "photos/image.jpg" from "chatfolder/photos/image.jpg"
          const pathWithoutChatFolder = cleanPath.replace(new RegExp(`^${chatFolderName.replace(/\\/g, '/')}/`), '');
          
          // Extract just the filename
          const justFilename = filename.split('/').pop().split('\\').pop();
          
          // Store by various patterns for maximum compatibility
          mediaFileMap[filename] = url;
          mediaFileMap[cleanPath] = url;
          mediaFileMap[relativePath] = url;
          mediaFileMap[pathWithoutChatFolder] = url;
          mediaFileMap[justFilename] = url;
          mediaFileMap[`photos/${justFilename}`] = url;
          mediaFileMap[`videos/${justFilename}`] = url;
          mediaFileMap[`audio/${justFilename}`] = url;
          
          // Also add with backslashes for Windows compatibility
          mediaFileMap[pathWithoutChatFolder.replace(/\//g, '\\')] = url;
          mediaFileMap[`photos\\${justFilename}`] = url;
          mediaFileMap[`videos\\${justFilename}`] = url;
          mediaFileMap[`audio\\${justFilename}`] = url;
          
          console.log(`Mapped media file: ${justFilename} -> ${pathWithoutChatFolder}`);
          
          // Categorize media
          if (lowerFilename.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/)) {
            images.push({ url, name: justFilename, path: cleanPath });
          } else if (lowerFilename.match(/\.(mp4|webm|ogg|mov|avi)$/)) {
            videos.push({ url, name: justFilename, path: cleanPath });
          }
        }
      });
      
      console.log(`Chat ${chatFolderName}: ${Object.keys(mediaFileMap).length} media mappings, ${images.length} images, ${videos.length} videos`);

      // Read and parse HTML file
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const htmlContent = event.target.result;
          const parsedMessages = parseInstagramHTML(htmlContent, myUsername, mediaFileMap);
          
          if (parsedMessages.length === 0) {
            console.log('No messages found in', chatFolderName);
            resolve(null);
            return;
          }
          
          // Generate chat ID using crypto API if available, otherwise fall back to Date.now
          let chatId;
          if (typeof crypto !== 'undefined' && crypto.randomUUID) {
            chatId = crypto.randomUUID();
          } else {
            chatId = chatFolderName + '_' + Date.now() + '_' + Math.random().toString(36).substring(7);
          }
          
          const chatData = {
            id: chatId,
            folderName: chatFolderName,
            messages: parsedMessages,
            mediaFiles: mediaFileMap,
            images: images,
            videos: videos,
            lastMessage: parsedMessages[parsedMessages.length - 1],
            messageCount: parsedMessages.length
          };
          
          resolve(chatData);
        } catch (err) {
          console.error('Error parsing chat', chatFolderName, ':', err);
          resolve(null);
        }
      };
      reader.readAsText(htmlFile);
    });
  };
  
  // Select and display a specific chat
  const selectChat = (chatId) => {
    const chat = chats.find(c => c.id === chatId);
    if (chat) {
      setSelectedChatId(chatId);
      setMessages(chat.messages);
      setMediaFiles(chat.mediaFiles);
      setAllImages(chat.images);
      setAllVideos(chat.videos);
      setFolderName(chat.folderName);
    }
  };
  
  // Original process files function for backward compatibility
  const processFiles = (files) => {
    // Check if this looks like an inbox folder with multiple chat folders
    const hasMultipleHtmlFiles = files.filter(f => f.name.toLowerCase().endsWith('.html')).length > 1;
    
    if (hasMultipleHtmlFiles) {
      // Process as inbox folder with multiple chats
      processInboxFolder(files);
    } else {
      // Process as single chat folder (original behavior)
      processSingleChatLegacy(files);
    }
  };
  
  const processSingleChatLegacy = (files) => {
    // Find the HTML file
    const htmlFile = files.find(f => f.name.toLowerCase().endsWith('.html'));
    if (!htmlFile) {
      setError('Please select an HTML file along with media files.');
      return;
    }

    // Extract folder name from file path
    let extractedFolderName = 'Chat';
    if (files.length > 0 && files[0].webkitRelativePath) {
      const pathParts = files[0].webkitRelativePath.split('/');
      if (pathParts.length > 1) {
        extractedFolderName = pathParts[pathParts.length - 2] || pathParts[0];
        const cleanParts = stripInstagramPrefix(files[0].webkitRelativePath).split('/');
        if (cleanParts.length > 1) {
          extractedFolderName = cleanParts[0];
        }
      }
    }
    setFolderName(extractedFolderName);

    // Create object URLs for media files and categorize them
    const mediaFileMap = {};
    const images = [];
    const videos = [];
    
    files.forEach(file => {
      if (!file.name.toLowerCase().endsWith('.html')) {
        const url = URL.createObjectURL(file);
        const filename = file.name;
        const lowerFilename = filename.toLowerCase();
        
        const relativePath = file.webkitRelativePath || filename;
        const cleanPath = stripInstagramPrefix(relativePath);
        
        mediaFileMap[filename] = url;
        mediaFileMap[cleanPath] = url;
        mediaFileMap[relativePath] = url;
        
        const justFilename = filename.split('/').pop().split('\\').pop();
        mediaFileMap[justFilename] = url;
        mediaFileMap[`photos/${justFilename}`] = url;
        mediaFileMap[`videos/${justFilename}`] = url;
        mediaFileMap[`audio/${justFilename}`] = url;
        
        if (lowerFilename.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/)) {
          images.push({ url, name: justFilename, path: cleanPath });
        } else if (lowerFilename.match(/\.(mp4|webm|ogg|mov|avi)$/)) {
          videos.push({ url, name: justFilename, path: cleanPath });
        }
      }
    });
    
    console.log('Loaded media files:', Object.keys(mediaFileMap).length);
    console.log('Images:', images.length, 'Videos:', videos.length);
    setMediaFiles(mediaFileMap);
    setAllImages(images);
    setAllVideos(videos);

    // Read and parse HTML file
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const htmlContent = event.target.result;
        const parsedMessages = parseInstagramHTML(htmlContent, myUsername, mediaFileMap);
        
        if (parsedMessages.length === 0) {
          setError('No messages found in the file. Please check the file format.');
          setFileLoaded(false);
        } else {
          setMessages(parsedMessages);
          setFileLoaded(true);
          setError('');
        }
      } catch (err) {
        console.error('Error parsing file:', err);
        setError('Error parsing file: ' + err.message);
        setFileLoaded(false);
      }
    };
    reader.readAsText(htmlFile);
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    processFiles(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const items = e.dataTransfer.items;
    const files = [];

    // Process dropped items
    const processEntry = (entry, path = '') => {
      return new Promise((resolve) => {
        if (entry.isFile) {
          entry.file((file) => {
            // Add webkitRelativePath for consistency
            Object.defineProperty(file, 'webkitRelativePath', {
              value: path + file.name,
              writable: false
            });
            files.push(file);
            resolve();
          });
        } else if (entry.isDirectory) {
          const dirReader = entry.createReader();
          dirReader.readEntries(async (entries) => {
            for (const subEntry of entries) {
              await processEntry(subEntry, path + entry.name + '/');
            }
            resolve();
          });
        } else {
          resolve();
        }
      });
    };

    const processItems = async () => {
      for (let i = 0; i < items.length; i++) {
        const item = items[i].webkitGetAsEntry();
        if (item) {
          await processEntry(item);
        }
      }
      if (files.length > 0) {
        processFiles(files);
      }
    };

    processItems();
  };

  const formatTime = (dateObj) => {
    if (!dateObj || isNaN(dateObj)) return 'Invalid Date';
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
      timeZone: timezone
    }).format(dateObj);
  };

  const openFullscreen = (media, msgIdx, mediaIdx) => {
    setFullscreenMedia({ ...media, msgIdx, mediaIdx });
  };

  const closeFullscreen = () => {
    setFullscreenMedia(null);
  };

  const changePlaybackSpeed = (videoRef, speed) => {
    if (videoRef && videoRef.current) {
      videoRef.current.playbackRate = speed;
    }
  };
  
  if (!fileLoaded) {
    return (
      <div 
        className="setup-screen"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <h1 style={{textAlign:'center'}}>Instagram Chat Viewer</h1>
        
        <label>1. Enter YOUR Username (to identify your messages)</label>
        <input 
          type="text" 
          placeholder="e.g. johndoe" 
          value={myUsername}
          onChange={(e) => setMyUsername(e.target.value)}
        />
        
        <label>2. Upload folder or files (HTML + images/videos/audio)</label>
        <div className={`drop-zone ${isDragging ? 'dragging' : ''}`}>
          <input 
            ref={fileInputRef}
            type="file" 
            multiple 
            webkitdirectory=""
            directory=""
            accept=".html,image/*,video/*,audio/*" 
            onChange={handleFileUpload}
            style={{display: 'none'}}
          />
          <button 
            className="upload-btn"
            onClick={() => fileInputRef.current?.click()}
          >
            Choose Folder
          </button>
          <div className="drop-zone-text">
            or drag and drop folder here
          </div>
        </div>
        <div style={{fontSize:'0.8rem', color:'#666', marginTop:'8px'}}>
          Drag and drop the entire Instagram export folder, or click to select files
        </div>
        
        {error && (
          <div style={{fontSize:'0.9rem', color:'#ff4444', padding:'10px', backgroundColor:'#2a1111', borderRadius:'8px'}}>
            {error}
          </div>
        )}
        
        <div style={{fontSize:'0.8rem', color:'#888'}}>
          Note: This processes files locally in your browser. No data is uploaded.
        </div>
      </div>
    );
  }

  // Helper function to determine chat display name based on participants
  const determineChatName = (messagesArray, chatFolderName) => {
    if (!messagesArray || messagesArray.length === 0) return 'Unknown Chat';
    
    // Get all unique sender names from messages
    const uniqueSenders = new Set(messagesArray.map(m => m.sender).filter(Boolean));
    const uniqueSenderCount = uniqueSenders.size;
    
    // If more than 2 unique names, use folder name (letters only, capitalize first letter)
    if (uniqueSenderCount > 2) {
      // Extract letters only from folder name
      const lettersOnly = chatFolderName.replace(/[^a-zA-Z]/g, '');
      // Capitalize first letter
      const formattedName = lettersOnly.charAt(0).toUpperCase() + lettersOnly.slice(1).toLowerCase();
      return formattedName || 'Group Chat';
    } else {
      // Use the other person's name (not the user's)
      const chatPartner = messagesArray.find(m => !m.isMine)?.sender || "Instagram User";
      return chatPartner;
    }
  };
  
  // Determine chat name for a given chat object
  const getChatDisplayName = (chat) => {
    if (!chat || !chat.messages) return 'Unknown Chat';
    return determineChatName(chat.messages, chat.folderName);
  };
  
  // Determine chat name based on number of unique participants (for selected chat)
  const getDisplayChatName = () => {
    return determineChatName(messages, folderName);
  };
  
  const displayChatName = getDisplayChatName();
  
  // Safe function to get avatar initials
  const getAvatarInitials = (name) => {
    if (!name || name.length === 0) return '??';
    if (name.length === 1) return name.toUpperCase() + '?';
    return name.substring(0, 2).toUpperCase();
  };
  
  // Check if we have multiple chats loaded
  const hasMultipleChats = chats.length > 1;

  return (
    <div className="app-container-with-sidebar">
      {/* Chat List Sidebar (only show if multiple chats) */}
      {hasMultipleChats && (
        <div className="chat-list-sidebar">
          <div className="chat-list-header">
            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
              <span>Messages</span>
              <ArrowLeft 
                size={24} 
                style={{cursor:'pointer'}} 
                onClick={() => {
                  setFileLoaded(false);
                  setChats([]);
                  setSelectedChatId(null);
                }} 
              />
            </div>
          </div>
          <div className="chat-list-items">
            {chats.map(chat => {
              const chatName = getChatDisplayName(chat);
              const lastMsg = chat.lastMessage;
              const isActive = chat.id === selectedChatId;
              
              return (
                <div 
                  key={chat.id} 
                  className={`chat-list-item ${isActive ? 'active' : ''}`}
                  onClick={() => selectChat(chat.id)}
                >
                  <div className="chat-list-avatar">
                    {getAvatarInitials(chatName)}
                  </div>
                  <div className="chat-list-info">
                    <div className="chat-list-name">{chatName}</div>
                    <div className="chat-list-preview">
                      {lastMsg?.content || (lastMsg?.media?.length > 0 ? '📷 Photo/Video' : 'No messages')}
                    </div>
                  </div>
                  <div className="chat-list-meta">
                    <div className="chat-list-count">{chat.messageCount} msgs</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Main Chat Area */}
      <div className="app-container">
      {/* Header */}
      <div className="chat-header">
        <div className="header-user">
          <ArrowLeft 
            size={24} 
            style={{cursor:'pointer'}} 
            onClick={() => {
              if (hasMultipleChats) {
                // In multi-chat view, just deselect
                setSelectedChatId(null);
                setMessages([]);
              } else {
                // In single chat view, go back to setup
                setFileLoaded(false);
                setChats([]);
              }
            }} 
          />
          <div className="avatar-placeholder">{getAvatarInitials(displayChatName)}</div>
          <div className="username">{displayChatName}</div>
        </div>
        <div className="header-icons">
          <Phone size={24} />
          <Video size={24} />
          <Info size={24} />
        </div>
      </div>

      {/* Timezone Switcher Overlay */}
      <div className="controls">
        <select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
          {TIMEZONES.map(tz => (
            <option key={tz.value} value={tz.value}>{tz.label}</option>
          ))}
        </select>
      </div>

      {/* Chat Area */}
      <div className="chat-scroll-area">
        {messages.length === 0 ? (
          <div style={{textAlign:'center', color:'#888', padding:'40px'}}>
            No messages to display. The file may be empty or in an unsupported format.
          </div>
        ) : (
          messages.map((msg, idx) => {
            const isMine = msg.isMine;
            // Show sender name for messages from others (not mine)
            const showSenderName = !isMine;
            return (
              <div key={idx} className={`message-row ${isMine ? 'mine' : 'theirs'}`}>
                {showSenderName && (
                  <div className="sender-name">{msg.sender}</div>
                )}
                <div className={`bubble ${isMine ? 'mine' : 'theirs'}`}>
                  {msg.content && <div className="message-content">{msg.content}</div>}
                  {msg.media && msg.media.length > 0 && (
                    <div className="message-media">
                      {msg.media.map((media, mediaIdx) => {
                        const videoKey = `${idx}-${mediaIdx}`;
                        if (!videoRefs.current[videoKey]) {
                          videoRefs.current[videoKey] = React.createRef();
                        }
                        
                        if (media.type === 'image') {
                          return (
                            <div key={mediaIdx} className="media-box image-box">
                              <img 
                                src={media.url} 
                                alt={media.alt || 'Image'} 
                                className="media-image"
                                onClick={() => openFullscreen(media, idx, mediaIdx)}
                              />
                              <button 
                                className="fullscreen-btn"
                                onClick={() => openFullscreen(media, idx, mediaIdx)}
                                title="View fullscreen"
                              >
                                <Maximize2 size={16} />
                              </button>
                            </div>
                          );
                        } else if (media.type === 'video') {
                          return (
                            <div key={mediaIdx} className="media-box video-box">
                              <video 
                                ref={videoRefs.current[videoKey]}
                                src={media.url} 
                                controls 
                                className="media-video"
                                controlsList="nodownload"
                              />
                              <div className="video-controls">
                                <button 
                                  className="speed-btn"
                                  onClick={() => changePlaybackSpeed(videoRefs.current[videoKey], 0.5)}
                                  title="0.5x speed"
                                >
                                  0.5x
                                </button>
                                <button 
                                  className="speed-btn"
                                  onClick={() => changePlaybackSpeed(videoRefs.current[videoKey], 1)}
                                  title="Normal speed"
                                >
                                  1x
                                </button>
                                <button 
                                  className="speed-btn"
                                  onClick={() => changePlaybackSpeed(videoRefs.current[videoKey], 1.5)}
                                  title="1.5x speed"
                                >
                                  1.5x
                                </button>
                                <button 
                                  className="speed-btn"
                                  onClick={() => changePlaybackSpeed(videoRefs.current[videoKey], 2)}
                                  title="2x speed"
                                >
                                  2x
                                </button>
                                <button 
                                  className="fullscreen-btn"
                                  onClick={() => openFullscreen(media, idx, mediaIdx)}
                                  title="View fullscreen"
                                >
                                  <Maximize2 size={16} />
                                </button>
                              </div>
                            </div>
                          );
                        } else if (media.type === 'audio') {
                          return (
                            <div key={mediaIdx} className="media-box audio-box">
                              <audio 
                                src={media.url} 
                                controls 
                                className="media-audio"
                                controlsList="nodownload"
                              />
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  )}
                  <div className="message-time">
                    {formatTime(msg.timestamp)}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Fullscreen Media Modal */}
      {fullscreenMedia && (
        <div className="fullscreen-modal" onClick={closeFullscreen}>
          <button className="close-fullscreen-btn" onClick={closeFullscreen}>
            <X size={32} />
          </button>
          <div className="fullscreen-content" onClick={(e) => e.stopPropagation()}>
            {fullscreenMedia.type === 'image' && (
              <img 
                src={fullscreenMedia.url} 
                alt={fullscreenMedia.alt || 'Image'} 
                className="fullscreen-image"
              />
            )}
            {fullscreenMedia.type === 'video' && (
              <video 
                src={fullscreenMedia.url} 
                controls 
                autoPlay
                className="fullscreen-video"
                controlsList="nodownload"
              />
            )}
          </div>
        </div>
      )}
      </div>
      
      {/* Media Sidebar (Right Side) */}
      {(allImages.length > 0 || allVideos.length > 0) && (
        <div className="media-sidebar media-sidebar-right">
          <div className="sidebar-header">Media</div>
          
          {allImages.length > 0 && (
            <div className="sidebar-section">
              <div className="sidebar-section-title">Images ({allImages.length})</div>
              <div className="sidebar-media-grid">
                {allImages.map((img, idx) => (
                  <div 
                    key={idx} 
                    className="sidebar-media-item"
                    onClick={() => openFullscreen({ type: 'image', url: img.url, alt: img.name }, -1, idx)}
                  >
                    <img src={img.url} alt={img.name} />
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {allVideos.length > 0 && (
            <div className="sidebar-section">
              <div className="sidebar-section-title">Videos ({allVideos.length})</div>
              <div className="sidebar-media-grid">
                {allVideos.map((vid, idx) => (
                  <div 
                    key={idx} 
                    className="sidebar-media-item"
                    onClick={() => openFullscreen({ type: 'video', url: vid.url }, -1, idx)}
                  >
                    <video src={vid.url} />
                    <div className="video-overlay">▶</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default App;